import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleSwitch } from '../../common';
import { useUserStore } from '@auw/stores';
import { MARKETING_VERSION } from '../../../lib/legalVersions';
import { useCookieConsent } from '../../../lib/cookieConsent';
import { useFeatureFlags } from '@auw/feature-flags';
import { activeTenantAppName, getActiveTenantManifest } from '@auw/tenant';
import {
  Divider,
  FieldLabel,
  FieldRow,
  FieldValue,
  SectionBlock,
  SectionHeader,
  SectionTitle,
  SettingDesc,
  SettingInfo,
  SettingName,
  SettingRow,
} from '../styles';

interface ServiceTabProps {
  onClose: () => void;
}

export const ServiceTab: React.FC<ServiceTabProps> = ({ onClose }) => {
  const { t } = useTranslation('settings');
  const consents = useUserStore((s) => s.user?.consents);
  const updateConsents = useUserStore((s) => s.updateConsents);

  /* P11-5c: tenant-policy gates for the service settings tab. */
  const featureFlags = useFeatureFlags();
  const marketingConsentEnabled = featureFlags.isEnabled('settings.consents.marketing');
  const cookieConsentEnabled = featureFlags.isEnabled('settings.consents.cookies');
  const productName = getActiveTenantManifest()?.id === 'shi' ? 'SOLA-COWORK' : activeTenantAppName();

  const [marketing, setMarketing] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const { categories, acceptSelected } = useCookieConsent();
  const [cookieAnalytics, setCookieAnalytics] = useState(categories.analytics);
  const [cookieMarketing, setCookieMarketing] = useState(categories.marketing);

  useEffect(() => {
    setCookieAnalytics(categories.analytics);
    setCookieMarketing(categories.marketing);
  }, [categories.analytics, categories.marketing]);

  useEffect(() => {
    setMarketing(consents?.marketing?.agreed ?? false);
  }, [consents?.marketing?.agreed]);

  const toggleMarketing = useCallback(async (val: boolean) => {
    setMarketing(val);
    setSyncing(true);
    try {
      await updateConsents([{ consent_type: 'marketing', agreed: val, version: MARKETING_VERSION }]);
    } catch {
      setMarketing(!val);
    } finally {
      setSyncing(false);
    }
  }, [updateConsents]);

  void onClose;

  return (
    <>
      {marketingConsentEnabled && (
        <SectionBlock>
          <SectionHeader>
            <SectionTitle>{t('service.marketingConsent')}</SectionTitle>
          </SectionHeader>
          <Divider />

          <SettingRow>
            <SettingInfo>
              <SettingName>{t('service.marketingConsentTitle')}</SettingName>
              <SettingDesc>{t('service.marketingConsentDesc')}</SettingDesc>
            </SettingInfo>
            <ToggleSwitch checked={marketing} onChange={toggleMarketing} disabled={syncing} />
          </SettingRow>
        </SectionBlock>
      )}

      {cookieConsentEnabled && (
        <SectionBlock>
          <SectionHeader>
            <SectionTitle>{t('service.cookieSettings')}</SectionTitle>
          </SectionHeader>
          <Divider />

          <SettingRow>
            <SettingInfo>
              <SettingName>{t('service.cookieAnalyticsTitle')}</SettingName>
              <SettingDesc>{t('service.cookieAnalyticsDesc')}</SettingDesc>
            </SettingInfo>
            <ToggleSwitch
              checked={cookieAnalytics}
              onChange={(val) => {
                setCookieAnalytics(val);
                acceptSelected({ analytics: val, marketing: cookieMarketing });
              }}
            />
          </SettingRow>
          <SettingRow>
            <SettingInfo>
              <SettingName>{t('service.cookieMarketingTitle')}</SettingName>
              <SettingDesc>{t('service.cookieMarketingDesc')}</SettingDesc>
            </SettingInfo>
            <ToggleSwitch
              checked={cookieMarketing}
              onChange={(val) => {
                setCookieMarketing(val);
                acceptSelected({ analytics: cookieAnalytics, marketing: val });
              }}
            />
          </SettingRow>
        </SectionBlock>
      )}

      <SectionBlock>
        <SectionHeader>
          <SectionTitle>{t('service.oauthInfo')}</SectionTitle>
        </SectionHeader>
        <Divider />

        <FieldRow>
          <FieldLabel>{t('service.appName')}</FieldLabel>
          <FieldValue>{productName}</FieldValue>
        </FieldRow>
        <FieldRow>
          <FieldLabel>{t('service.version')}</FieldLabel>
          <FieldValue>1.0.0</FieldValue>
        </FieldRow>
        <FieldRow>
          <FieldLabel>{t('service.provider')}</FieldLabel>
          <FieldValue>TEAMREBOOTT Inc.</FieldValue>
        </FieldRow>
      </SectionBlock>
    </>
  );
};
